import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Quota } from '../../../quotas/models/quota.interface';
import { QuotaService } from '../../../quotas/services/quota.service';
import { AuthService } from '../../../../core/services/auth.service';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { QuotaStatusPipe } from '../../../../shared/pipes/quota-status.pipe';
import { getRussianPaginatorIntl } from '../../../../core/providers/russian-paginator-intl';
import { PaginationService } from '../../../../core/services/pagination.service';
import { QuotaSummaryBarComponent } from '../../../../shared/components/quota-summary-bar/quota-summary-bar';
import { QuotaStatusDialogComponent } from '../../components/quota-status-dialog/quota-status-dialog.component';
import { AddLimitQuotaDialogComponent } from '../../components/add-limit-quota-dialog/add-limit-quota-dialog.component';

@Component({
  selector: 'app-quotas-admin',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatChipsModule,
    MatDialogModule,
    MatTooltipModule,
    KgToTonsPipe,
    QuotaStatusPipe,
    QuotaSummaryBarComponent,
    TranslateModule
  ],
  providers: [
    { provide: MatPaginatorIntl, useValue: getRussianPaginatorIntl() }
  ],
  templateUrl: './quotas-admin.html',
  styleUrl: './quotas-admin.css',
})
export class QuotasAdminComponent implements OnInit {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private paginationService = inject(PaginationService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);

  // Данные
  quotas = signal<Quota[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  // Пагинация
  pagination = this.paginationService.createPaginationState(15);

  // Проверка наличия квот на текущий год
  currentYearQuotasExist = computed(() => {
    const currentYear = new Date().getFullYear();
    return this.quotas().some(q => q.year === currentYear);
  });

  // Проверка роли - quota_manager может только просматривать
  isManager = computed(() => {
    return this.authService.hasRole('quota_viewer') || (this.authService.hasRole('quota_manager') && !this.authService.hasRole('quota_operator'));
  });

  // Колонки таблицы
  displayedColumns = computed(() => {
    const columns = [
      'id',
      'year',
      'status',
      'meatType',
      'totalLimitVolume',
      'allocatedVolume',
      'remainderVolume'
    ];

    // Добавляем колонку действий только если НЕ менеджер
    if (!this.isManager()) {
      columns.push('actions');
    }

    return columns;
  });

  ngOnInit(): void {
    this.loadQuotas();
  }

  loadQuotas(): void {
    this.loading.set(true);
    this.error.set(null);

    const params = this.pagination.getParams();

    this.quotaService.getAllQuotas(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.quotas.set(response.res.content || []);
          this.pagination.setTotalElements(response.res.totalElements || 0);
        } else {
          this.error.set(response.msg || 'Ошибка загрузки данных');
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading quotas:', err);
        this.error.set('Не удалось загрузить квоты');
        this.loading.set(false);
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pagination.onPageChange(event);
    this.loadQuotas();
  }

  getStatusClass(status: string): string {
    const classMap: Record<string, string> = {
      'ACTIVE': 'status-active',
      'COMPLETED': 'status-completed',
      'EXPIRED': 'status-expired',
      'USED': 'status-used'
    };
    return classMap[status] || '';
  }

  addQuotaLimits(): void {
    const dialogRef = this.dialog.open(AddLimitQuotaDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadQuotas();
      }
    });
  }

  editQuota(quota: Quota): void {
    const dialogRef = this.dialog.open(QuotaStatusDialogComponent, {
      width: '500px',
      data: { quota }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadQuotas();
      }
    });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
